import React, { useState } from 'react';
import { UserAccount } from '../types';
import { changeUserPassword, ALL_RESTRICTED_AREAS } from '../utils/auth';

interface UserPasswordSettingsModalProps {
  isOpen: boolean;
  currentUser: UserAccount | null;
  onClose: () => void;
  onUserUpdated: (user: UserAccount) => void;
}

export function UserPasswordSettingsModal({
  isOpen,
  currentUser,
  onClose,
  onUserUpdated,
}: UserPasswordSettingsModalProps) {

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !currentUser) return null;

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!currentPassword) {
      setErrorMessage('Please enter your current password.');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirmation do not match.');
      return;
    }

    if (newPassword === currentPassword) {
      setErrorMessage('New password must be different from your current password.');
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      const res = changeUserPassword(currentUser.id, currentPassword, newPassword);

      if (!res.success) {
        setErrorMessage(res.message);
        setIsSubmitting(false);
        return;
      }

      if (res.updatedUser) {
        onUserUpdated(res.updatedUser);
      }

      setSuccessMessage('✅ Your password has been successfully updated and secured!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsSubmitting(false);
    }, 400);
  };

  const formattedLastChanged = currentUser.passwordLastChanged
    ? new Date(currentUser.passwordLastChanged).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Default Initial Setting';

  const userAllowedAreas = ALL_RESTRICTED_AREAS.filter((area) =>
    currentUser.role === 'admin' || (currentUser.allowedRestrictedAreas && currentUser.allowedRestrictedAreas.includes(area.id))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-4 overflow-y-auto">
      <div className="bg-white text-gray-800 w-full max-w-md rounded-2xl shadow-2xl border border-amber-500/30 overflow-hidden relative animate-fade-in my-6">
        
        {/* Imperial Header */}
        <div className="bg-gradient-to-r from-[#0F5132] via-emerald-900 to-[#0F5132] p-5 text-white relative border-b border-amber-400/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl p-2 bg-amber-400/10 rounded-lg border border-amber-400/30">🔑</span>
            <div>
              <h3 className="font-serif font-black text-base tracking-wide text-amber-300">
                Password & Security Center
              </h3>
              <p className="text-[10px] text-emerald-200">
                Manage User Credentials & Access Clearances
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-emerald-200 hover:text-amber-300 font-bold text-lg w-8 h-8 rounded-full bg-emerald-900/60 border border-emerald-700 flex items-center justify-center transition-all cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4 text-xs">
          {/* User Account Summary Card */}
          <div className="bg-amber-50/80 p-3.5 rounded-xl border border-amber-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-2xl">{currentUser.avatar}</span>
              <div>
                <h4 className="font-bold text-gray-900 text-xs">{currentUser.name}</h4>
                <p className="text-[10px] text-gray-600 font-mono">{currentUser.email}</p>
                <p className="text-[9px] text-amber-900 font-semibold">{currentUser.title}</p>
              </div>
            </div>
            <span className="px-2 py-1 bg-emerald-900 text-amber-200 text-[9px] font-black uppercase rounded border border-emerald-700">
              {currentUser.role}
            </span>
          </div>

          {/* Security Status Card */}
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1.5 text-[10px]">
            <div className="flex justify-between items-center text-gray-700">
              <span className="font-bold text-gray-600">Password Status:</span>
              <span className={`font-bold px-1.5 py-0.5 rounded ${
                currentUser.mustChangePassword
                  ? 'bg-amber-100 text-amber-900 border border-amber-300'
                  : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
              }`}>
                {currentUser.mustChangePassword ? '🔑 Change Required' : '✓ Secured & Active'}
              </span>
            </div>
            <div className="flex justify-between items-center text-gray-700">
              <span className="font-bold text-gray-600">Last Changed:</span>
              <span className="font-mono text-gray-800">{formattedLastChanged}</span>
            </div>
          </div>

          {/* Allowed Restricted Areas Summary */}
          <div className="space-y-1.5">
            <span className="font-extrabold text-[#0F5132] uppercase tracking-wider text-[10px] block">
              🛡️ Authorized Exclusive Areas ({userAllowedAreas.length}):
            </span>
            <div className="flex flex-wrap gap-1">
              {userAllowedAreas.map((area) => (
                <span
                  key={area.id}
                  className="px-2 py-1 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded text-[9px] font-bold flex items-center gap-1"
                >
                  <span>{area.icon}</span>
                  <span>{area.name}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Change Password Form */}
          <form onSubmit={handlePasswordChange} className="space-y-3 pt-2 border-t border-gray-200">
            <h4 className="font-serif font-black text-gray-900 uppercase tracking-wider text-[11px] flex items-center gap-1">
              <span>🔒</span> Change Your Password
            </h4>

            {errorMessage && (
              <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-[11px] font-semibold rounded-lg flex items-center gap-2">
                <span>⚠️</span>
                <span>{errorMessage}</span>
              </div>
            )}

            {successMessage && (
              <div className="p-2.5 bg-emerald-50 border border-emerald-300 text-emerald-800 text-[11px] font-bold rounded-lg flex items-center gap-2">
                <span>✓</span>
                <span>{successMessage}</span>
              </div>
            )}

            <div>
              <label className="font-bold text-gray-700 uppercase tracking-wider block mb-1 text-[10px]">
                Current Password
              </label>
              <input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F5132] font-medium"
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 uppercase tracking-wider block mb-1 text-[10px]">
                New Password (Min. 6 characters)
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F5132] font-medium"
              />
            </div>

            <div>
              <label className="font-bold text-gray-700 uppercase tracking-wider block mb-1 text-[10px]">
                Confirm New Password
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F5132] font-medium"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-xl font-bold uppercase tracking-wider cursor-pointer text-xs"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-2 py-2.5 bg-[#0F5132] hover:bg-[#08331f] text-amber-300 rounded-xl font-black uppercase tracking-wider cursor-pointer shadow-md border border-amber-400/50 text-xs"
              >
                {isSubmitting ? 'Updating...' : 'Update Password 🔑'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UserPasswordSettingsModal;

